import React, { useState, useMemo, useEffect, useRef } from 'react';
import './Calendar.css';

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end?: Date;
  location?: string;
  type?: string;
  badges?: string[];
  organizer?: string;
}

interface CalendarProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onRangeChange?: (start: Date, end: Date) => void;
  weeks?: number;
  weekStartsOn?: 0 | 1;
}

interface PopupState {
  event: CalendarEvent;
  top: number;
  left: number;
}

const MS_PER_DAY = 86400000;
const DEFAULT_WEEKS = 5;

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const startOfWeek = (d: Date, weekStartsOn: 0 | 1) => {
  const x = startOfDay(d);
  const diff = (x.getDay() - weekStartsOn + 7) % 7;
  x.setDate(x.getDate() - diff);
  return x;
};

const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const monthLabel = (d: Date) =>
  d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

const formatTimeRange = (start: Date, end?: Date) => {
  const dateOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  const startStr = `${start.toLocaleDateString(undefined, dateOpts)} ${start.toLocaleTimeString(undefined, timeOpts)}`;
  if (!end) return startStr;
  const sameDay = isSameDay(start, end);
  const endStr = sameDay
    ? end.toLocaleTimeString(undefined, timeOpts)
    : `${end.toLocaleDateString(undefined, dateOpts)} ${end.toLocaleTimeString(undefined, timeOpts)}`;
  return `${startStr} – ${endStr}`;
};

const Calendar: React.FC<CalendarProps> = ({
  events,
  onEventClick,
  onRangeChange,
  weeks = DEFAULT_WEEKS,
  weekStartsOn = 1,
}) => {
  const defaultViewStart = () => addDays(startOfWeek(new Date(), weekStartsOn), -7);
  const [viewStart, setViewStart] = useState<Date>(defaultViewStart);
  const [popup, setPopup] = useState<PopupState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!popup) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setPopup(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopup(null);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [popup]);

  const handleEventBtnClick = (e: React.MouseEvent<HTMLButtonElement>, ev: CalendarEvent) => {
    e.stopPropagation();
    onEventClick?.(ev);
    const containerRect = containerRef.current?.getBoundingClientRect();
    const btnRect = e.currentTarget.getBoundingClientRect();
    if (containerRect) {
      setPopup({
        event: ev,
        top: btnRect.bottom - containerRect.top + 4,
        left: btnRect.left - containerRect.left,
      });
    }
  };

  const totalDays = weeks * 7;
  const today = startOfDay(new Date());

  useEffect(() => {
    onRangeChange?.(viewStart, addDays(viewStart, totalDays));
  }, [viewStart, totalDays, onRangeChange]);

  const days = useMemo(() => {
    return Array.from({ length: totalDays }, (_, i) => addDays(viewStart, i));
  }, [viewStart, totalDays]);

  const primaryMonth = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of days) {
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    let bestKey = '';
    let best = -1;
    counts.forEach((c, k) => {
      if (c > best) {
        best = c;
        bestKey = k;
      }
    });
    const [year, month] = bestKey.split('-').map(Number);
    return { year, month };
  }, [days]);

  const eventsByDay = useMemo(() => {
    const map = new Map<number, CalendarEvent[]>();
    const viewEnd = addDays(viewStart, totalDays);
    for (const ev of events) {
      const day = startOfDay(ev.start);
      if (day < viewStart || day >= viewEnd) continue;
      const key = day.getTime();
      const list = map.get(key) || [];
      list.push(ev);
      map.set(key, list);
    }
    return map;
  }, [events, viewStart, totalDays]);

  const dayHeaders = useMemo(() => {
    const ref = startOfWeek(new Date(), weekStartsOn);
    return Array.from({ length: 7 }, (_, i) =>
      addDays(ref, i).toLocaleDateString(undefined, { weekday: 'short' })
    );
  }, [weekStartsOn]);

  const rangeLabel = useMemo(() => {
    const last = addDays(viewStart, totalDays - 1);
    if (viewStart.getMonth() === last.getMonth()) return monthLabel(viewStart);
    return `${monthLabel(viewStart)} – ${monthLabel(last)}`;
  }, [viewStart, totalDays]);

  const goPrevWeek = () => setViewStart((d) => addDays(d, -7));
  const goNextWeek = () => setViewStart((d) => addDays(d, 7));
  const goToday = () => setViewStart(defaultViewStart());

  return (
    <div className="cal" ref={containerRef}>
      <div className="cal-toolbar">
        <div className="cal-toolbar-left">
          <button type="button" className="cal-btn" onClick={goPrevWeek} aria-label="Previous week">‹</button>
          <button type="button" className="cal-btn" onClick={goNextWeek} aria-label="Next week">›</button>
          <button type="button" className="cal-btn cal-btn-today" onClick={goToday}>today</button>
        </div>
        <h2 className="cal-title">{rangeLabel}</h2>
        <div className="cal-toolbar-right" />
      </div>

      <div className="cal-grid">
        <div className="cal-row cal-header">
          {dayHeaders.map((label) => (
            <div key={label} className="cal-cell cal-day-header">{label}</div>
          ))}
        </div>

        {Array.from({ length: weeks }, (_, weekIdx) => (
          <div key={weekIdx} className="cal-row">
            {days.slice(weekIdx * 7, weekIdx * 7 + 7).map((day) => {
              const isToday = isSameDay(day, today);
              const isOtherMonth = day.getMonth() !== primaryMonth.month || day.getFullYear() !== primaryMonth.year;
              const dayEvents = eventsByDay.get(day.getTime()) || [];
              return (
                <div
                  key={day.getTime()}
                  className={`cal-cell cal-day${isToday ? ' cal-today' : ''}${isOtherMonth ? ' cal-other-month' : ''}`}
                >
                  <div className="cal-day-number">{day.getDate()}</div>
                  <div className="cal-day-events">
                    {dayEvents.map((ev) => (
                      <button
                        key={ev.id}
                        type="button"
                        className="cal-event"
                        onClick={(e) => handleEventBtnClick(e, ev)}
                        title={ev.title}
                      >
                        {ev.title}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {popup && (
        <div
          className="cal-popup"
          style={{ top: popup.top, left: popup.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="cal-popup-header">
            <h4 className="cal-popup-title">
              <a className="cal-popup-title-link" href={`/event/${popup.event.id}`}>
                {popup.event.title}
              </a>
            </h4>
            <button
              type="button"
              className="cal-popup-close"
              onClick={() => setPopup(null)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="cal-popup-body">
            <p className="cal-popup-row">🕒 {formatTimeRange(popup.event.start, popup.event.end)}</p>
            {popup.event.location && (
              <p className="cal-popup-row">📍 {popup.event.location}</p>
            )}
            {popup.event.type && (
              <p className="cal-popup-row">🏷️ {popup.event.type}</p>
            )}
            {popup.event.organizer && (
              <p className="cal-popup-row">👤 {popup.event.organizer}</p>
            )}
            {popup.event.badges && popup.event.badges.length > 0 && (
              <div className="cal-popup-badges">
                {popup.event.badges.map((b) => (
                  <span key={b} className="cal-popup-badge">{b}</span>
                ))}
              </div>
            )}
            <a className="cal-popup-details-btn" href={`/event/${popup.event.id}`}>
              View details
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
